import { zodResolver } from '@hookform/resolvers/zod'
import { format } from 'date-fns'
import compare from 'just-compare'
import filterObject from 'just-filter-object'
import { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { z } from 'zod'

import { selectOrderFilters } from '@/ducks/orders/selectors/orders.selectors'
import {
  OrderFilters,
  setOrderFilters
} from '@/ducks/orders/slices/orders.slice'

const formSchema = z.object({
  start_date: z.date().nullish(),
  end_date: z.date().nullish(),
  start_price: z.number().nullish(),
  end_price: z.number().nullish(),
  statuses: z.array(z.string()),
  brand: z.string()
})
type FormData = z.infer<typeof formSchema>

const filtersDefaultValues = {
  start_date: null,
  end_date: null,
  start_price: null,
  end_price: null,
  statuses: [],
  brand: ''
}

const useOrdersFilterForm = (shouldResetForm: boolean) => {
  const dispatch = useDispatch()

  const filters = useSelector(selectOrderFilters)

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: filtersDefaultValues
  })
  const {
    reset,
    control,
    setValue,
    handleSubmit,
    watch,
    formState,
    getValues
  } = form

  const formData = watch()
  const { start_date: startDate, end_date: endDate } = formData

  const shouldDisableSubmit = !formState.isDirty
  const shouldDisableReset = compare(getValues(), filtersDefaultValues)

  useEffect(
    function resetDirtyStatus() {
      if (shouldResetForm && formState.isSubmitted) reset(getValues())
    },
    [formState.isSubmitted, getValues, reset, shouldResetForm]
  )

  useEffect(
    function syncFormWithStore() {
      const { start_date, end_date, statuses, ...restFilters } = filters
      reset({
        ...filtersDefaultValues,
        ...restFilters,
        ...(start_date ? { start_date: new Date(start_date) } : {}),
        ...(end_date ? { end_date: new Date(end_date) } : {}),
        ...(statuses ? { statuses } : { statuses: [] })
      })
    },
    [filters, reset]
  )

  const onReset = () => {
    Object.entries(filtersDefaultValues).forEach(([key, value]) =>
      setValue(key as keyof typeof filtersDefaultValues, value, {
        shouldDirty: true
      })
    )
    sendFormData(filtersDefaultValues)
  }

  const sendFormData = useCallback(
    (data: FormData) => {
      const cleanedData = filterObject(
        data,
        (_, value) => value !== undefined && value !== null
      )

      const { start_date, end_date, statuses, brand, ...restCleanData } =
        cleanedData
      const { start_price, end_price } = cleanedData

      const normalizedData = {
        ...restCleanData,
        ...(brand ? { brand } : {}),
        ...(start_date ? { start_date: format(start_date, 'yyyy-MM-dd') } : {}),
        ...(end_date ? { end_date: format(end_date, 'yyyy-MM-dd') } : {}),
        ...(statuses && statuses.length > 0 ? { statuses } : {})
      }

      if (start_price && end_price && start_price > end_price) {
        setValue('start_price', end_price)
        setValue('end_price', start_price)
        normalizedData.start_price = end_price
        normalizedData.end_price = start_price
      }

      dispatch(
        setOrderFilters({
          ...normalizedData
        } as OrderFilters)
      )
    },
    [dispatch, setValue]
  )

  const onSubmit = useMemo(
    () => handleSubmit(sendFormData),
    [handleSubmit, sendFormData]
  )

  return {
    shouldDisableSubmit,
    startDate,
    endDate,
    onReset,
    onSubmit,
    control,
    form,
    shouldDisableReset
  } as const
}

export { useOrdersFilterForm }
